import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Select } from '@ds';
import styles from './UpdateDocumentModal.module.scss';
import { IOption } from '@ds/elements/Select/Select.types';
import { useGetDocumentTypes } from 'src/shared/queries/useGetDocumentTypes';
import { useGetDocument } from 'src/shared/queries/useGetDocument';
import StaticFileIcon from '@icons/static-file.svg';
import UpdatePassportDetails, { PASSPORT_FIELD } from './UpdatePassportDetails.tsx/UpdatePassportDetails';
import { DocumentData } from 'src/shared/types/Document';
import { DefaultDateFormat } from 'src/utils/constants/common';
import { format } from 'date-fns';
import classNames from 'classnames';
import { useGetUser } from 'src/shared/queries/useGetUser';

interface UpdateDocumentModalProps {
  documentId: string | null;
  onClose: () => void;
  onUpdate: (document: DocumentData) => void;
  documentTypeName: string;
}

const MODAL_TITLE = 'UPDATE';
const DOCTYPE_PASSPORT = 'Passport';
const TITLES = {
  updateDocumentDetails: 'UPDATE DOCUMENT DETAILS',
  updatePassportDetails: 'UPDATE PASSPORT DETAILS',
  docName: 'Document Name',
  docType: 'Document Type',
  docVisibility: 'Document Visibility',
};

const UpdateDocumentModal: React.FC<UpdateDocumentModalProps> = ({
  documentId,
  onClose,
  onUpdate,
  documentTypeName,
}) => {
  const [documentName, setDocumentName] = useState('');
  const [selectedDocType, setSelectedDocType] = useState<IOption | null>(null);
  const { data: dataDocumentTypes, isLoading: isLoadingDocumentTypes } = useGetDocumentTypes();
  const [selectedDocument, setSelectedDocument] = useState<DocumentData>();
  const [isValidPassportNumber, setIsValidPassportNumber] = useState<boolean>(true);
  const {
    data: documentData,
    isLoading: isLoadingDocument,
    isRefetching: isRefetchingDocument,
  } = useGetDocument(documentId || '');
  const { data: userInfo } = useGetUser();
  const documentFiles = documentData?.files || [];
  const documentTypes = dataDocumentTypes || [];
  const documentTypeId = documentData?.docTypeId;
  const isArchivedDocumentType = !documentTypes.find((dt) => dt.docTypeId == documentTypeId);
  const displayedDocumentTypes =
    isArchivedDocumentType && documentTypeId
      ? [...documentTypes, { docTypeId: documentTypeId, docTypeName: documentTypeName }]
      : documentTypes;
  const initialDocumentData = useRef<DocumentData | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const isLoading = isLoadingDocumentTypes || isLoadingDocument || isRefetchingDocument;

  useEffect(() => {
    setHasChanges(checkIfFieldsChanged());
  }, [documentName, selectedDocType, selectedDocument]);

  const handleChangeDocType = (selectedOption: IOption | null) => {
    setSelectedDocType(selectedOption);
  };

  const handleDocumentNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDocumentName(e.target.value);
  };

  const handleUpdate = () => {
    const docName = documentName;
    const docTypeId = selectedDocType?.value as number;
    const updatedDocument: DocumentData = {
      ...(documentData as DocumentData),
      docName,
      docTypeId,
      beneId: userInfo?.beneId,
      companyId: userInfo?.companyId,
      fields: { ...selectedDocument?.fields },
    };
    onUpdate(updatedDocument);
  };

  const memoizedDocumentTypes = useMemo(
    () => displayedDocumentTypes?.map((document) => ({ value: document.docTypeId, label: document.docTypeName })) || [],
    [displayedDocumentTypes]
  );

  const populateInitialValues = () => {
    if (!documentData) {
      return;
    }

    setDocumentName(documentData.docName);
    setSelectedDocType({ value: documentData.docTypeId, label: documentData.docTypeName });
    setSelectedDocument(documentData);

    initialDocumentData.current = documentData;
  };

  const handleFieldsChanged = (
    fieldName: string,
    fieldValue: string | number | boolean | Date | null,
    isValid = true
  ) => {
    if (fieldName === PASSPORT_FIELD.issuedTo) {
      setSelectedDocument((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [fieldName]: fieldValue,
        };
      });
      return;
    }

    if (fieldName === PASSPORT_FIELD.passportNumber) {
      setIsValidPassportNumber(isValid);
    }

    if (fieldValue instanceof Date) {
      fieldValue = format(fieldValue as Date, DefaultDateFormat);
    }

    setSelectedDocument((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        fields: {
          ...prev.fields,
          [fieldName]: fieldValue,
        },
      };
    });
  };

  const checkIfFieldsChanged = () => {
    if (!initialDocumentData.current || !selectedDocument) {
      return true;
    }

    if (
      documentName !== initialDocumentData.current.docName ||
      selectedDocType?.value !== initialDocumentData.current.docTypeId
    ) {
      return true;
    }

    const initialFields = initialDocumentData.current.fields || {};
    const currentFields = selectedDocument.fields || {};

    return Object.keys(initialFields).some(
      (key) => initialFields[key as keyof typeof initialFields] !== currentFields[key as keyof typeof currentFields]
    );
  };

  const checkShouldDisableUpdateButton = () => {
    const selectedDocTypeName = selectedDocType
      ? documentTypes.find((dt) => dt.docTypeId == selectedDocType?.value)?.docTypeName
      : '';
    return (
      isLoading ||
      !documentData ||
      !documentName ||
      !selectedDocType ||
      !hasChanges ||
      (selectedDocTypeName == DOCTYPE_PASSPORT &&
        (!isValidPassportNumber || !selectedDocument?.fields?.passportNumber)) ||
      (selectedDocTypeName == DOCTYPE_PASSPORT && !selectedDocument?.fields?.passportExpirationDate)
    );
  };

  useEffect(() => {
    populateInitialValues();
  }, [documentData]);

  const isPassport = useMemo(
    () => documentTypes.find((dt) => dt.docTypeId == selectedDocType?.value)?.docTypeName == DOCTYPE_PASSPORT,
    [documentTypes, selectedDocType]
  );

  const shouldDisableUpdateButton = checkShouldDisableUpdateButton();

  return (
    <>
      <Modal
        showLoadingOverlay={isLoading}
        visible
        onModalClosed={onClose}
        className={'w-full md:!w-2/3 lg:!w-[881px]'}
        title={MODAL_TITLE}
      >
        <div>
          <div
            className={classNames(styles['update-document-details'], 'max-h-[250px] lg:!max-h-[660px] overflow-y-auto')}
          >
            <div className="grid grid-cols-1 gap-[20px]">
              <div className="md:w-[calc(50%-10px)]">
                <Input value={documentName} onChange={handleDocumentNameChange} label={TITLES.docName} required />
              </div>
              <div className="md:w-[calc(50%-10px)]">
                <Select
                  searchable
                  label={TITLES.docType}
                  selectedOption={selectedDocType}
                  onChange={handleChangeDocType}
                  options={memoizedDocumentTypes}
                  appendTo={document.body}
                  required
                />
              </div>
            </div>

            <div className={styles['files-list']}>
              {documentFiles.map((file, index) => (
                <div key={index} className={styles['file-item']}>
                  <img src={StaticFileIcon} className={styles['file-icon']} />
                  <p className={styles['file-name']}>{file}</p>
                </div>
              ))}
            </div>
            {isPassport && selectedDocument && (
              <UpdatePassportDetails
                documentData={selectedDocument}
                disableCountry={!!documentData?.fields?.country?.trim()}
                disableIssuedTo={!!documentData?.beneFullName?.trim()}
                onPassportDetailsChanged={handleFieldsChanged}
              />
            )}
          </div>
          <div className={'flex flex-col-reverse mt-[16px] gap-[16px] w-full md:flex-row md:float-right md:w-[unset]'}>
            <Button
              variant="outlined"
              size="medium"
              styleType="danger"
              onClick={onClose}
              className="md:!text-[14px] w-full md:w-[unset] !h-[40px] md:!h-[32px]"
            >
              CANCEL
            </Button>
            <Button
              styleType="primary"
              onClick={handleUpdate}
              className="md:!text-[14px] w-full md:w-[unset] !h-[40px] md:!h-[32px]"
              disabled={shouldDisableUpdateButton}
            >
              UPDATE
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UpdateDocumentModal;
